import { useEffect, useMemo, useRef, useState } from "react";
import { IGetListItemsProps, IGetTermsProps } from "../../Service/IUserProfile";
import {
  addListItem,
  getCounty,
  getListItems,
  getRegions,
  getStates,
  getTerms,
  getUniversities,
  updateListItems,
  updateUserProfileForCurrentUser,
  validateRoutingNumber,
  validateNPI,
  getCountries,
} from "../../Service/Services";
import {
  FormDropdownOption,
  FormField,
  VisibilityCondition,
} from "./FormTypes";
import { toBool, toOptionsForField, countries } from "./Utility";

// Date helpers to ensure local date-only serialization (no UTC shifting)
const isDateOnlyString = (s: any): s is string =>
  typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.trim());

const formatLocalDateYYYYMMDD = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const coerceToLocalDate = (v: any): Date | undefined => {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (isDateOnlyString(v)) {
    const [y, m, d] = v.split("-");
    const date = new Date();
    date.setFullYear(Number(y), Number(m) - 1, Number(d));
    date.setHours(0, 0, 0, 0);
    return date;
  }
  const parsed = new Date(v);
  return isNaN(parsed.getTime()) ? undefined : parsed;
};

export const useInitialValues = (fields: FormField[]) => {
  const normalizeInit = (f: FormField) => {
    if (f.type === "Checkbox") return toBool(f.value);
    if ((f.type === "List" || f.type === "PrioritizedList") && f.multi) {
      const v = (f as any).value;
      if (Array.isArray(v)) return v;
      if (v === undefined || v === null || String(v).trim() === "") return [];
      if (typeof v === "string") {
        const s = v.trim();
        if (
          (s.startsWith("[") && s.endsWith("]")) ||
          (s.startsWith('"') && s.endsWith('"'))
        ) {
          try {
            const parsed = JSON.parse(s);
            return Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            return s
              .split(/[;,|]/)
              .map((x) => x.trim())
              .filter(Boolean);
          }
        }
        return s
          .split(/[;,|]/)
          .map((x) => x.trim())
          .filter(Boolean);
      }
      return [v];
    }
    return f.value ?? "";
  };

  const [values, setValues] = useState<Record<string, any>>(() => {
    const init: Record<string, any> = {};
    fields.forEach((f) => {
      const v = normalizeInit(f);
      if ((f as any)._originalValue === undefined) {
        (f as any)._originalValue = v;
      }
      init[f.id] = v;
    });
    return init;
  });

  const lastInitialRef = useRef<Record<string, any>>(() => {
    const m: Record<string, any> = {};
    fields.forEach((f) => (m[f.id] = normalizeInit(f)));
    return m;
  }) as React.MutableRefObject<Record<string, any>>;

  useEffect(() => {
    // Update untouched fields when underlying initial values change (e.g., async userProfile load)
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      const nextInitials: Record<string, any> = {};
      fields.forEach((f) => {
        const newInit = normalizeInit(f);
        nextInitials[f.id] = newInit;
        const lastInit = lastInitialRef.current[f.id];
        const prevVal = prev[f.id];

        if (prevVal === undefined) {
          next[f.id] = newInit;
          changed = true;
          // Update _originalValue only when first initialized
          if ((f as any)._originalValue === undefined) {
            (f as any)._originalValue = newInit;
          }
          return;
        }

        // If the user hasn't changed the field (value equals last init), refresh it
        const prevStr = JSON.stringify(prevVal);
        const lastStr = JSON.stringify(lastInit);
        const newStr = JSON.stringify(newInit);
        if (prevStr === lastStr && lastStr !== newStr) {
          next[f.id] = newInit;
          changed = true;
          // Update _originalValue when initial value changes and user hasn't touched the field
          (f as any)._originalValue = newInit;
        } else if ((f as any)._originalValue === undefined) {
          // Safety fallback: ensure _originalValue is always set
          (f as any)._originalValue = newInit;
        }
      });
      lastInitialRef.current = nextInitials;
      return changed ? next : prev;
    });
  }, [fields]);

  return [values, setValues] as const;
};

const checkConditions = (
  field: FormField,
  values: Record<string, any>,
  conditions?: VisibilityCondition[],
) => {
  const conds = conditions ?? field.visibilityConditions;
  if (!conds || conds.length === 0) return field.visible !== false;

  return conds.every((c) => {
    const left = values[c.fieldId];
    switch (c.operator) {
      case "checked":
        return toBool(left) === true;
      case "notChecked":
        return toBool(left) === false;
      case "equals": {
        const rhs = typeof c.value === "boolean" ? toBool(c.value) : c.value;
        const lhs = typeof c.value === "boolean" ? toBool(left) : left;
        return lhs === rhs;
      }
      case "notEquals": {
        const rhs = typeof c.value === "boolean" ? toBool(c.value) : c.value;
        const lhs = typeof c.value === "boolean" ? toBool(left) : left;
        return lhs !== rhs;
      }
      default:
        return true;
    }
  });
};

export const useVisibility = (
  fields: FormField[],
  values: Record<string, any>,
) => {
  const [visibleFields, setVisibleFields] = useState<FormField[]>(fields);
  useEffect(() => {
    const updated = fields.map((f) => ({
      ...f,
      visible: checkConditions(f, values),
    }));
    setVisibleFields(updated);
  }, [fields, values]);
  return visibleFields;
};

export const useListOptions = (
  siteUrl: string,
  fields: FormField[],
  userState?: string,
) => {
  const [fieldOptions, setFieldOptions] = useState<
    Record<string, FormDropdownOption[]>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const fetchedRef = useRef<Set<string>>(new Set());
  const inflightRef = useRef<Map<string, Promise<FormDropdownOption[]>>>(
    new Map(),
  );

  const signature = useMemo(
    () =>
      JSON.stringify(
        fields
          .filter(
            (f) =>
              f.visible !== false &&
              (f.type === "List" || f.type === "PrioritizedList") &&
              f.listName &&
              !f.options,
          )
          .map((f) => [f.id, f.listName, f.columnNames, f.dropdownText]),
      ),
    [fields],
  );

  const fieldsRef = useRef<FormField[]>(fields);
  const fieldOptionsRef =
    useRef<Record<string, FormDropdownOption[]>>(fieldOptions);

  useEffect(() => {
    fieldsRef.current = fields;
    fieldOptionsRef.current = fieldOptions;
  }, [fields, fieldOptions]);

  useEffect(() => {
    let cancelled = false;

    const listFields = fieldsRef.current.filter(
      (f) =>
        f.visible !== false &&
        (f.type === "List" || f.type === "PrioritizedList") &&
        f.listName &&
        !f.options,
    );
    if (listFields.length === 0) return;

    const keyOf = (f: FormField) =>
      `${String(f.listName)}|${JSON.stringify(f.columnNames ?? [])}|${
        f.id
      }|${JSON.stringify(f.dropdownText ?? "")}${
        f.listName === "County" ? `|${userState ?? ""}` : ""
      }`;

    setIsLoading(true);

    Promise.all(
      listFields.map(async (f) => {
        const key = keyOf(f);

        if (fetchedRef.current.has(key)) {
          return {
            fieldId: f.id,
            options: fieldOptionsRef.current[f.id] ?? [],
          };
        }
        if (!inflightRef.current.has(key)) {
          const promise: Promise<FormDropdownOption[]> =
            f.listName === "State"
              ? getStates().then((res) => {
                  const raw = Array.isArray(res?.data)
                    ? res.data
                    : (res?.data?.items ?? res?.data?.value ?? []);
                  return toOptionsForField(f.dropdownText, raw);
                })
              : f.listName === "Regions"
                ? getRegions().then((res) => {
                    const raw = Array.isArray(res?.data)
                      ? res.data
                      : (res?.data?.items ?? res?.data?.value ?? []);
                    return toOptionsForField(f.dropdownText, raw);
                  })
                : f.listName === "County"
                  ? getCounty(userState || "").then((res: any) => {
                      const raw = Array.isArray(res?.data)
                        ? res.data
                        : (res?.data?.items ?? res?.data?.value ?? []);
                      return toOptionsForField(f.dropdownText, raw);
                    })
                  : f.listName === "Universities"
                    ? getUniversities().then((res) => {
                        const raw = Array.isArray(res?.data)
                          ? res.data
                          : (res?.data?.items ?? res?.data?.value ?? []);
                        return toOptionsForField(f.dropdownText, raw);
                      })
                    : f.listName === "Countries"
                      ? getCountries().then((res) => {
                          const raw = Array.isArray(res?.data)
                            ? res.data
                            : (res?.data?.items ?? res?.data?.value ?? []);
                          return toOptionsForField(f.dropdownText, raw);
                        })
                      : f.listName === "Languages"
                        ? getTerms({
                            siteUrl,
                            groupName: "PBS",
                            termSetName: "Languages",
                          } as IGetTermsProps).then((res) => {
                            const raw = Array.isArray(res?.data)
                              ? res.data
                              : (res?.data?.items ?? res?.data?.value ?? []);
                            return toOptionsForField(f.dropdownText, raw);
                          })
                        : f.listName === "BusinessClassifications"
                          ? getTerms({
                              siteUrl,
                              groupName: "Business",
                              termSetName: "TaxInfo",
                            } as IGetTermsProps).then((res) => {
                              const raw = Array.isArray(res?.data)
                                ? res.data
                                : (res?.data?.items ?? res?.data?.value ?? []);
                              return toOptionsForField(f.dropdownText, raw);
                            })
                          : f.listName === "Degree"
                            ? getTerms({
                                siteUrl,
                                groupName: "PBS",
                                termSetName: "Degree",
                              } as IGetTermsProps).then((res) => {
                                const raw = Array.isArray(res?.data)
                                  ? res.data
                                  : (res?.data?.items ??
                                    res?.data?.value ??
                                    []);
                                return toOptionsForField(f.dropdownText, raw);
                              })
                            : f.listName === "InsuranceCompanies"
                              ? getTerms({
                                  siteUrl,
                                  groupName: "Business",
                                  termSetName:
                                    "Previous Credentialed Insurance Companies",
                                } as IGetTermsProps).then((res) => {
                                  const raw = Array.isArray(res?.data)
                                    ? res.data
                                    : (res?.data?.items ??
                                      res?.data?.value ??
                                      []);
                                  return toOptionsForField(f.dropdownText, raw);
                                })
                              : getListItems({
                                  siteUrl,
                                  listName: f.listName,
                                  viewFields: f.columnNames,
                                  rowLimit: 200,
                                } as IGetListItemsProps).then((res) => {
                                  const raw = Array.isArray(res?.data)
                                    ? res.data
                                    : (res?.data?.items ??
                                      res?.data?.value ??
                                      []);
                                  return toOptionsForField(f.dropdownText, raw);
                                });
          inflightRef.current.set(key, promise);
        }

        const options = await inflightRef.current.get(key)!;
        fetchedRef.current.add(key);
        return { fieldId: f.id, options };
      }),
    )
      .then((results) => {
        if (cancelled) return;
        setFieldOptions((prev) => {
          const next = { ...prev };
          results.forEach(({ fieldId, options }) => {
            next[fieldId] = options;
          });
          return next;
        });
      })
      .finally(() => !cancelled && setIsLoading(false));

    return () => {
      cancelled = true;
    };
  }, [siteUrl, signature, userState]); // stable

  return { fieldOptions, isLoading };
};

// Overloads for compatibility
export type UpdateResult = {
  type: "success" | "info" | "warning" | "error";
  message: string;
};

export async function handleSave(
  fields: FormField[],
  formValues: Record<string, any>,
  siteUrl: string,
  pbsUser: string,
  fieldOptions?: Record<string, any[]>,
): Promise<UpdateResult>;
export async function handleSave(
  fields: FormField[],
  siteUrl: string,
  pbsUser: string,
): Promise<UpdateResult>;
export async function handleSave(
  fields: FormField[],
  a: any,
  b?: any,
  c?: any,
  d?: any,
): Promise<UpdateResult> {
  const EducationHistoryProps: Record<string, any> = {};
  const WorkHistoryProps: Record<string, any> = {};
  const errors: string[] = [];

  let eduCity: string | undefined;
  let fieldOptions: Record<string, any[]> = d || {};
  let eduState: string | undefined;
  let eduCityChanged = false;
  let eduStateChanged = false;

  let formValues: Record<string, any> | null = null;
  let siteUrl: string | undefined = undefined;
  let pbsUser: string | undefined = undefined;

  // Resolve args for both signatures
  if (typeof a === "object" && a !== null && typeof b === "string") {
    formValues = a as Record<string, any>;
    siteUrl = b as string;
    pbsUser = c as string;
  } else if (typeof a === "string" && b === undefined) {
    siteUrl = a as string;
  } else {
    siteUrl = b as string;
    formValues = null;
  }

  if (!siteUrl || typeof siteUrl !== "string") {
    const e: any = new Error("Site URL is required");
    e.type = "warning";
    throw e;
  }

  const pickOptionValue = (opt: any) =>
    opt?.key ?? opt?.text ?? opt?.value ?? opt;
  const digitsOnly = (val: any) => String(val ?? "").replace(/\D+/g, "");
  const matchCountryForPhone = (raw: any) => {
    const s = String(raw ?? "").trim();
    return countries.find((c) => s.startsWith(c.code));
  };
  const normalizeForApi = (field: FormField, raw: any) => {
    if (raw === undefined) return undefined;
    if (field.type === "Date") {
      const d = coerceToLocalDate(raw);
      return d ? formatLocalDateYYYYMMDD(d) : undefined;
    }
    if (field.type === "Checkbox") return toBool(raw);
    // Handle PeoplePicker: return comma-separated string of user IDs
    if (field.type === "PeoplePicker") {
      if (typeof raw === "object" && raw !== null && "results" in raw) {
        // Unwrap { results: [...] } to comma-separated string
        if (Array.isArray(raw.results)) {
          return raw.results
            .map((v: any) => String(v))
            .filter(Boolean)
            .join(",");
        }
        return "";
      }
      // Already an array - join as comma-separated
      if (Array.isArray(raw)) {
        return raw
          .map((v) => String(v))
          .filter(Boolean)
          .join(",");
      }
      // Single value - return as string
      return raw ? String(raw) : "";
    }
    // Handle List fields: send the Name/text instead of the ID
    if (field.type === "List") {
      const options = fieldOptions[field.id] || [];
      const findOptionName = (id: any) => {
        const opt = options?.find(
          (o: any) =>
            o.key === id || o.value === id || o.ID === id || o.id === id,
        );
        return opt?.text || opt?.Name || opt?.name || id;
      };

      if (field.multi && Array.isArray(raw)) {
        return raw.map((id) => findOptionName(id));
      } else {
        return findOptionName(raw);
      }
    }
    // Handle PrioritizedList: already contains text values, send semicolon-delimited for SharePoint
    if (field.type === "PrioritizedList") {
      if (field.multi && Array.isArray(raw)) {
        return raw.filter(Boolean).join(";"); // Join with semicolon for SharePoint multi-value format
      } else if (Array.isArray(raw) && raw.length > 0) {
        return raw[0];
      }
      return raw;
    }
    // For other multi-select or array fields, return array as-is
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return pickOptionValue(raw);
    return raw;
  };
  const normalizeForCompare = (field: FormField, raw: any) => {
    if (raw === undefined || raw === null) return "";
    if (field.type === "Date") {
      const d = coerceToLocalDate(raw);
      return d ? formatLocalDateYYYYMMDD(d) : "";
    }
    if (field.type === "Checkbox") return String(toBool(raw));
    if (field.type === "Phone") return digitsOnly(raw);
    if (field.type === "PeoplePicker") {
      // Multi-select: { results: [...] }
      if (typeof raw === "object" && raw !== null && "results" in raw) {
        const arr = Array.isArray(raw.results)
          ? raw.results.map((v: any) => String(v)).filter(Boolean)
          : [];
        return arr.join(",");
      }
      // Single-select: id string
      return raw ? String(raw).trim() : "";
    }
    if (Array.isArray(raw)) {
      const arr = raw.map((v) => pickOptionValue(v)).map((x) => String(x));
      // For PrioritizedList, order matters - don't sort. For others, sort for consistency.
      if (field.type === "PrioritizedList") {
        return arr.join("|");
      }
      return arr.slice().sort().join("|");
    }
    if (typeof raw === "object") return String(pickOptionValue(raw));
    return String(raw).trim();
  };

  const getCertificationRegex = (analystLevel: string): RegExp => {
    switch (String(analystLevel || "").trim()) {
      case "BCaBA":
        return /^0-\d{2}-\d{4,}$/; // Format: "0-XX-XXXX..."
      case "BCBA":
      case "BCBA-D":
        return /^1-\d{2}-\d{4,}$/; // Format: "1-XX-XXXX..."
      case "RBT":
        return /^RBT-\d{2}-\d{4,}$/; // Format: "RBT-XX-XXXX..."
      default:
        return /.*/; // Allow anything if analyst level is unknown
    }
  };

  const isCertificationRequired = (analystLevel: string): boolean => {
    const a = String(analystLevel || "").trim();
    return a === "BCaBA" || a === "BCBA" || a === "BCBA-D" || a === "RBT";
  };

  const getCertificationPatternMessage = (analystLevel: string): string => {
    switch (String(analystLevel || "").trim()) {
      case "BCaBA":
        return "0-XX-XXXX... (e.g., 0-12-3456)";
      case "BCBA":
      case "BCBA-D":
        return "1-XX-XXXX... (e.g., 1-12-3456)";
      case "RBT":
        return "RBT-XX-XXXX... (e.g., RBT-12-3456)";
      default:
        return "BCaBA → 0-XX-XXXX..., BCBA/BCBA-D → 1-XX-XXXX..., RBT → RBT-XX-XXXX...";
    }
  };

  const mappingsFor = (field: FormField) => {
    if (!field.destinations) return [];
    return Array.isArray(field.destinations)
      ? field.destinations
      : [field.destinations];
  };

  fields.forEach((field) => {
    const currentRaw = formValues ? formValues[field.id] : (field as any).value;
    // Initialize _originalValue on first encounter if not already set
    if ((field as any)._originalValue === undefined) {
      (field as any)._originalValue = currentRaw;
    }
    const originalRaw = (field as any)._originalValue;

    const apiValue = normalizeForApi(field, currentRaw);
    const currComparable = normalizeForCompare(field, currentRaw);
    const origComparable = normalizeForCompare(field, originalRaw);

    const changed = currComparable !== origComparable;

    // Validate required fields cannot be empty
    // Skip validation if field is not visible
    // For new fields (no original value): only error if user never entered anything
    // For existing fields (has original value): error if user cleared it
    if (field.required && field.visible !== false) {
      const hasOriginalValue =
        originalRaw !== undefined &&
        originalRaw !== null &&
        (typeof originalRaw !== "string" || originalRaw.trim() !== "");

      // Only coerce to date if field is a Date type
      const d =
        field.type === "Date" ? coerceToLocalDate(currentRaw) : undefined;

      // Check if field is empty, accounting for different value types
      let isEmpty = false;
      if (
        field.type === "PeoplePicker" ||
        (field.type === "List" && field.multi) ||
        field.type === "PrioritizedList"
      ) {
        // Multi-select PeoplePicker or List: { results: [...] }
        if (typeof currentRaw === "object" && "results" in currentRaw) {
          isEmpty =
            !Array.isArray(currentRaw.results) ||
            currentRaw.results.length === 0;
        } else if (Array.isArray(currentRaw)) {
          isEmpty = currentRaw.length === 0;
        } else {
          isEmpty = !currentRaw;
        }
      } else {
        // Standard types
        isEmpty =
          currentRaw === undefined ||
          currentRaw === null ||
          (typeof currentRaw === "string" && currentRaw.trim() === "") ||
          (field.type === "Date" && d === undefined);
      }

      // Error if: (1) field had data and user cleared it, OR (2) field is empty and unchanged (user never entered data)
      if (isEmpty && (hasOriginalValue || currComparable === origComparable)) {
        errors.push(`Required: ${field.title} cannot be empty`);
      }
    }

    // Validate BACB ID format
    if (field.type === "Text" && field.id === "applicantBACBID") {
      const BACBIDRegex = /^BACB\d{6}$/;
      // this should be the value format e.g. BACB123456
      if (typeof currentRaw === "string" && currentRaw.trim() !== "") {
        if (!BACBIDRegex.test(currentRaw.trim())) {
          errors.push(
            `${field.title}: please enter a valid BACB ID in the format BACBXXXXXX (ex. BACB123456)`,
          );
        }
      }
    }

    // Validate Certification Number based on Analyst Level
    if (field.type === "Text" && field.id === "certification#") {
      const analystLevel = formValues
        ? formValues["analystLevel"]
        : (fields.find((f) => f.id === "analystLevel") as any)?.value;

      const required = isCertificationRequired(String(analystLevel ?? ""));
      const s = String(currentRaw ?? "").trim();

      if (required && s === "") {
        errors.push(`Required: ${field.title} cannot be empty`);
      } else if (s !== "") {
        const regex = getCertificationRegex(String(analystLevel ?? ""));

        if (!regex.test(s)) {
          errors.push(
            `${field.title}: invalid format for ${analystLevel}. Expected: ${getCertificationPatternMessage(String(analystLevel ?? ""))}`,
          );
        }
      }
    }

    // Validate phone fields only when the phone value changed, using country-specific rules
    if (field.type === "Phone" && changed) {
      const s = String(currentRaw ?? "").trim();
      const country = matchCountryForPhone(s);
      const local = country ? s.slice(country.code.length).trim() : s;
      const localDigits = local.replace(/\D+/g, "");

      if (country) {
        const { digits, minDigits, maxDigits, name } = country as any;
        if (typeof digits === "number" && localDigits.length !== digits) {
          errors.push(
            `${field.title}: please enter a ${digits}-digit phone number for ${name}`,
          );
        } else if (
          typeof minDigits === "number" &&
          typeof maxDigits === "number" &&
          (localDigits.length < minDigits || localDigits.length > maxDigits)
        ) {
          errors.push(
            `${field.title}: please enter a ${minDigits}-${maxDigits} digit phone number for ${name}`,
          );
        }
      } else {
        // Fallback: require between 6 and 15 digits (E.164 local range varies)
        if (localDigits.length < 6 || localDigits.length > 15) {
          errors.push(
            `${field.title}: please enter a valid phone number (6-15 digits)`,
          );
        }
      }
    }

    // Validate character limit for fields that have characterLimit property
    if (field.characterLimit && currentRaw) {
      const s = String(currentRaw ?? "").trim();
      if (s.length > field.characterLimit) {
        errors.push(
          `${field.title}: must not exceed ${field.characterLimit} characters (current: ${s.length})`,
        );
      }
    }

    mappingsFor(field).forEach((m: any) => {
      // Special handling for Education History WorkAddress: always capture
      // the latest City/State values, but only mark WorkAddress as changed
      // when at least one of the two fields actually changed.
      if (
        m?.destination === "educationHistory" &&
        m?.fieldName === "WorkAddress"
      ) {
        if (field.id === "City") {
          eduCity = typeof apiValue === "string" ? apiValue : undefined;
          if (changed) {
            eduCityChanged = true;
          }
        } else if (field.id === "State") {
          eduState = typeof apiValue === "string" ? apiValue : undefined;
          if (changed) {
            eduStateChanged = true;
          }
        }
        // Do not write WorkAddress directly here; it will be composed
        // from eduCity/eduState after all fields are processed.
        return;
      }

      if (!changed) return;

      if (m?.destination === "educationHistory" && m?.fieldName) {
        if (apiValue !== undefined) {
          EducationHistoryProps[m.fieldName] = apiValue;
        }
      }
      if (m?.destination === "workHistory" && m?.fieldName) {
        if (apiValue !== undefined) {
          WorkHistoryProps[m.fieldName] = apiValue;
        }
      }
      // Note: userProfile destination isn't applicable for add operations
    });
  });

  if (errors.length) {
    const e: any = new Error(errors.join(" | "));
    e.type = "warning";
    throw e;
  }

  // Combine Education History City + State into WorkAddress
  // Only when at least one of them actually changed to avoid
  // unnecessary updates.
  if ((eduCityChanged || eduStateChanged) && (eduCity || eduState)) {
    const parts: string[] = [];
    if (eduCity) parts.push(eduCity);
    if (eduState) parts.push(eduState);
    const combined = parts.join(", ");
    if (combined) {
      EducationHistoryProps["WorkAddress"] = combined;
    }
  }

  // If no values detected to add, surface friendly info
  if (
    Object.keys(EducationHistoryProps).length === 0 &&
    Object.keys(WorkHistoryProps).length === 0
  ) {
    return { type: "info", message: "No Changes Detected" };
  }

  try {
    if (Object.keys(EducationHistoryProps).length > 0) {
      await addListItem({
        siteUrl,
        listName: "Schools and Education",
        fieldValues: {
          ...EducationHistoryProps,
          pbsUser,
        },
      });
    }
    if (Object.keys(WorkHistoryProps).length > 0) {
      await addListItem({
        siteUrl,
        listName: "Work Experience",
        fieldValues: {
          ...WorkHistoryProps,
          pbsUser,
        },
      });
    }
  } catch (err: any) {
    const e: any = new Error(
      err?.message || "Error adding item. Please try again.",
    );
    e.type = "error";
    throw e;
  }

  const addedTargets: string[] = [];
  if (Object.keys(EducationHistoryProps).length > 0)
    addedTargets.push("Education History");
  if (Object.keys(WorkHistoryProps).length > 0)
    addedTargets.push("Work History");

  const subject = addedTargets.length
    ? addedTargets.length === 1
      ? addedTargets[0]
      : `${addedTargets.slice(0, -1).join(", ")} and ${addedTargets[addedTargets.length - 1]}`
    : "Item";
  const verb = addedTargets.length > 1 ? "have" : "has";
  const noun = addedTargets.length > 1 ? "items" : "item";
  return {
    type: "success",
    message: `${subject} ${noun} ${verb} been added successfully.`,
  };
}

export async function handleUpdate(
  fields: FormField[],
  formValues: Record<string, any>,
  loginName: string,
  itemId: number | any,
  siteUrl: string,
  fieldOptions?: Record<string, any[]>,
): Promise<UpdateResult>;
export async function handleUpdate(
  fields: FormField[],
  loginName: string,
  itemId: number | any,
  siteUrl: string,
): Promise<UpdateResult>;
export async function handleUpdate(
  fields: FormField[],
  loginName: string,
  siteUrl: string,
): Promise<UpdateResult>;
export async function handleUpdate(
  fields: FormField[],
  itemId: number | any,
  siteUrl: string,
): Promise<UpdateResult>;
export async function handleUpdate(
  fields: FormField[],
  a: any,
  b: any,
  c?: any,
  d?: any,
  e?: any,
): Promise<UpdateResult> {
  const userProfileProps: Record<string, any> = {};
  const DFCProps: Record<string, any> = {};
  const EducationHistoryProps: Record<string, any> = {};
  const WorkHistoryProps: Record<string, any> = {};
  const ApplicantsProps: Record<string, any> = {};
  const PBAPProps: Record<string, any> = {};
  const RBTProps: Record<string, any> = {};
  const RBTCProps: Record<string, any> = {};
  const AWSRProps: Record<string, any> = {};
  const errors: string[] = [];

  let eduCity: string | undefined;
  let eduState: string | undefined;
  let eduCityChanged = false;
  let fieldOptions: Record<string, any[]> = e || {};
  let eduStateChanged = false;

  let formValues: Record<string, any> | null = null;
  let loginName: string | undefined = undefined;
  let siteUrl: string | undefined = undefined;
  let itemId: number | undefined = undefined;

  // Resolve args based on the concrete call patterns we actually use
  // 1) handleUpdate(fields, formValues, loginName, itemId, siteUrl)
  if (
    a &&
    typeof a === "object" &&
    !Array.isArray(a) &&
    typeof b === "string" &&
    (typeof c === "number" || c === undefined) &&
    (typeof d === "string" || d === undefined)
  ) {
    formValues = a as Record<string, any>;
    loginName = b as string;
    itemId = typeof c === "number" ? (c as number) : undefined;
    siteUrl = typeof d === "string" ? (d as string) : undefined;
  }
  // 2) handleUpdate(fields, loginName, itemId, siteUrl)
  else if (
    typeof a === "string" &&
    (typeof b === "number" || b === undefined) &&
    (typeof c === "string" || c === undefined)
  ) {
    loginName = a as string;
    itemId = typeof b === "number" ? (b as number) : undefined;
    siteUrl = typeof c === "string" ? (c as string) : undefined;
  }
  // 3) handleUpdate(fields, loginName, siteUrl)
  else if (typeof a === "string" && typeof b === "string" && c === undefined) {
    loginName = a as string;
    siteUrl = b as string;
  }
  // 4) handleUpdate(fields, itemId, siteUrl) - DFC-only style
  else if (typeof a === "number" && typeof b === "string" && c === undefined) {
    itemId = a as number;
    siteUrl = b as string;
  }
  // 5) handleUpdate(fields, siteUrl)
  else if (typeof a === "string" && b === undefined) {
    siteUrl = a as string;
  }
  const pickOptionValue = (opt: any) =>
    opt?.key ?? opt?.text ?? opt?.value ?? opt;

  const digitsOnly = (val: any) => String(val ?? "").replace(/\D+/g, "");

  const matchCountryForPhone = (raw: any) => {
    const s = String(raw ?? "").trim();
    return countries.find((c) => s.startsWith(c.code));
  };

  const normalizeForApi = (field: FormField, raw: any) => {
    if (raw === undefined) return undefined;
    if (field.type === "Date") {
      const d = coerceToLocalDate(raw);
      return d ? formatLocalDateYYYYMMDD(d) : undefined;
    }
    if (field.type === "Checkbox") return toBool(raw);
    // Handle PeoplePicker: return comma-separated string of user IDs
    if (field.type === "PeoplePicker") {
      if (typeof raw === "object" && raw !== null && "results" in raw) {
        // Unwrap { results: [...] } to comma-separated string
        if (Array.isArray(raw.results)) {
          return raw.results
            .map((v: any) => String(v))
            .filter(Boolean)
            .join(",");
        }
        return "";
      }
      // Already an array - join as comma-separated
      if (Array.isArray(raw)) {
        return raw
          .map((v) => String(v))
          .filter(Boolean)
          .join(",");
      }
      // Single value - return as string
      return raw ? String(raw) : "";
    }
    // Handle List fields: send the Name/text instead of the ID
    if (field.type === "List" || field.type === "PrioritizedList") {
      const options = fieldOptions[field.id] || [];
      const findOptionName = (id: any) => {
        const opt = options?.find(
          (o: any) =>
            o.key === id || o.value === id || o.ID === id || o.id === id,
        );
        return opt?.text || opt?.Name || opt?.name || id;
      };

      if (
        (field.multi || field.type === "PrioritizedList") &&
        Array.isArray(raw)
      ) {
        const mapped = raw.map((id) => findOptionName(id));
        // PrioritizedList with multi: join with semicolon for SharePoint format
        if (field.type === "PrioritizedList") {
          return mapped.filter(Boolean).join(";");
        }
        return mapped;
      } else {
        return findOptionName(raw);
      }
    }
    // For other multi-select or array fields, return array as-is
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return pickOptionValue(raw);
    return raw;
  };

  const normalizeForCompare = (field: FormField, raw: any) => {
    if (raw === undefined || raw === null) return "";
    if (field.type === "Date") {
      const d = coerceToLocalDate(raw);
      return d ? formatLocalDateYYYYMMDD(d) : "";
    }
    if (field.type === "Checkbox") return String(toBool(raw));
    if (field.type === "Phone") return digitsOnly(raw);
    if (field.type === "PeoplePicker") {
      // Multi-select: { results: [...] }
      if (typeof raw === "object" && raw !== null && "results" in raw) {
        const arr = Array.isArray(raw.results)
          ? raw.results.map((v: any) => String(v)).filter(Boolean)
          : [];
        return arr.join(",");
      }
      // Single-select: id string
      return raw ? String(raw).trim() : "";
    }
    if (Array.isArray(raw)) {
      const arr = raw.map((v) => pickOptionValue(v)).map((x) => String(x));
      // For PrioritizedList, order matters - don't sort. For others, sort for consistency.
      if (field.type === "PrioritizedList") {
        return arr.join("|");
      }
      return arr.slice().sort().join("|");
    }
    if (typeof raw === "object") return String(pickOptionValue(raw));
    return String(raw).trim();
  };

  const getCertificationRegex = (analystLevel: string): RegExp => {
    switch (String(analystLevel || "").trim()) {
      case "BCaBA":
        return /^0-\d{2}-\d{4,}$/; // Format: "0-XX-XXXX..."
      case "BCBA":
      case "BCBA-D":
        return /^1-\d{2}-\d{4,}$/; // Format: "1-XX-XXXX..."
      case "RBT":
        return /^RBT-\d{2}-\d{4,}$/; // Format: "RBT-XX-XXXX..."
      default:
        return /.*/; // Allow anything if analyst level is unknown
    }
  };

  const isCertificationRequired = (analystLevel: string): boolean => {
    const a = String(analystLevel || "").trim();
    return a === "BCaBA" || a === "BCBA" || a === "BCBA-D" || a === "RBT";
  };

  const getCertificationPatternMessage = (analystLevel: string): string => {
    switch (String(analystLevel || "").trim()) {
      case "BCaBA":
        return "0-XX-XXXX... (e.g., 0-12-3456...)";
      case "BCBA":
      case "BCBA-D":
        return "1-XX-XXXX... (e.g., 1-12-3456...)";
      case "RBT":
        return "RBT-XX-XXXX... (e.g., RBT-12-3456...)";
      default:
        return "Invalid format for analyst level.";
    }
  };

  const mappingsFor = (field: FormField) => {
    if (!field.destinations) return [];
    return Array.isArray(field.destinations)
      ? field.destinations
      : [field.destinations];
  };

  fields.forEach((field) => {
    const currentRaw = formValues ? formValues[field.id] : (field as any).value;
    // Initialize _originalValue on first encounter if not already set
    if ((field as any)._originalValue === undefined) {
      (field as any)._originalValue = currentRaw;
    }
    const originalRaw = (field as any)._originalValue;

    const apiValue = normalizeForApi(field, currentRaw);
    const currComparable = normalizeForCompare(field, currentRaw);
    const origComparable = normalizeForCompare(field, originalRaw);

    const changed = currComparable !== origComparable;

    // Validate required fields cannot be empty
    // Skip validation if field is not visible
    // For new fields (no original value): only error if user never entered anything
    // For existing fields (has original value): error if user cleared it
    if (field.required && field.visible !== false) {
      const hasOriginalValue =
        originalRaw !== undefined &&
        originalRaw !== null &&
        (typeof originalRaw !== "string" || originalRaw.trim() !== "");

      // Only coerce to date if field is a Date type
      const d =
        field.type === "Date" ? coerceToLocalDate(currentRaw) : undefined;

      // Check if field is empty, accounting for different value types
      let isEmpty = false;
      if (
        field.type === "PeoplePicker" ||
        (field.type === "List" && field.multi) ||
        field.type === "PrioritizedList"
      ) {
        // Multi-select PeoplePicker or List: { results: [...] }
        if (typeof currentRaw === "object" && "results" in currentRaw) {
          isEmpty =
            !Array.isArray(currentRaw.results) ||
            currentRaw.results.length === 0;
        } else if (Array.isArray(currentRaw)) {
          isEmpty = currentRaw.length === 0;
        } else {
          isEmpty = !currentRaw;
        }
      } else {
        // Standard types
        isEmpty =
          currentRaw === undefined ||
          currentRaw === null ||
          (typeof currentRaw === "string" && currentRaw.trim() === "") ||
          (field.type === "Date" && d === undefined);
      }

      // Error if: (1) field had data and user cleared it, OR (2) field is empty and unchanged (user never entered data)
      if (isEmpty && (hasOriginalValue || currComparable === origComparable)) {
        errors.push(`Required: ${field.title} cannot be empty`);
      }
    }

    // Validate SSN format (country-specific)
    if (field.id === "socialsecuritynumber" && (field.required || currentRaw)) {
      const s = String(currentRaw ?? "").trim();
      let valid = false;
      if (s) {
        const ssnCanadaRegex = /^\d{3}-\d{3}-\d{3}$/;
        const ssnUsRegex = /^\d{3}-\d{2}-\d{4}$/;

        if (ssnCanadaRegex.test(s) || ssnUsRegex.test(s)) {
          valid = true;
        }

        if (!valid) {
          errors.push(
            `${field.title}: Invalid format. Expected XXX-XX-XXXX (US) or XXX-XXX-XXX (Canada)`,
          );
        }
      }
    }

    // Validate routing number format (basic check before API validation)
    // Only validate if the field is being used (has a value or is required)
    if (field.id === "routingNumber" && (field.required || currentRaw)) {
      const s = String(currentRaw ?? "").trim();
      if (s && !/^\d{9}$/.test(s)) {
        errors.push(`${field.title}: Invalid format. Expected 9 digits.`);
      }
    }

    // Validate NPI format (basic check before API validation)
    // Only validate if the field is being used (has a value or is required)
    if (field.id === "npi" && (field.required || currentRaw)) {
      const s = String(currentRaw ?? "").trim();
      if (s && !/^\d{10}$/.test(s)) {
        errors.push(`${field.title}: Invalid format. Expected 10 digits.`);
      }
    }

    // Validate Certification Number based on Analyst Level
    if (field.type === "Text" && field.id === "certification#") {
      const analystLevel = formValues
        ? formValues["analystLevel"]
        : (fields.find((f) => f.id === "analystLevel") as any)?.value;

      const required = isCertificationRequired(String(analystLevel ?? ""));
      const s = String(currentRaw ?? "").trim();

      if (required && s === "") {
        errors.push(`Required: ${field.title} cannot be empty`);
      } else if (s !== "") {
        const regex = getCertificationRegex(String(analystLevel ?? ""));

        if (!regex.test(s)) {
          errors.push(
            `${field.title}: Invalid format for ${analystLevel}. Expected: ${getCertificationPatternMessage(String(analystLevel ?? ""))}`,
          );
        }
      }
    }

    // Validate phone fields only when the phone value changed, using country-specific rules
    if (field.type === "Phone" && changed) {
      const s = String(currentRaw ?? "").trim();
      const country = matchCountryForPhone(s);
      const local = country ? s.slice(country.code.length).trim() : s;
      const localDigits = local.replace(/\D+/g, "");

      if (country) {
        const { digits, minDigits, maxDigits, name } = country as any;
        if (typeof digits === "number" && localDigits.length !== digits) {
          errors.push(
            `${field.title}: please enter a ${digits}-digit phone number for ${name}`,
          );
        } else if (
          typeof minDigits === "number" &&
          typeof maxDigits === "number" &&
          (localDigits.length < minDigits || localDigits.length > maxDigits)
        ) {
          errors.push(
            `${field.title}: please enter a ${minDigits}-${maxDigits} digit phone number for ${name}`,
          );
        }
      } else {
        // Fallback: require between 6 and 15 digits (E.164 local range varies)
        if (localDigits.length < 6 || localDigits.length > 15) {
          errors.push(
            `${field.title}: please enter a valid phone number (6-15 digits)`,
          );
        }
      }
    }

    // Validate character limit for fields that have characterLimit property
    if (field.characterLimit && currentRaw) {
      const s = String(currentRaw ?? "").trim();
      if (s.length > field.characterLimit) {
        errors.push(
          `${field.title}: must not exceed ${field.characterLimit} characters (current: ${s.length})`,
        );
      }
    }

    mappingsFor(field).forEach((m: any) => {
      // Special handling for Education History WorkAddress: always capture
      // the latest City/State values, but only mark WorkAddress as changed
      // when at least one of the two fields actually changed.
      if (
        m?.destination === "educationHistory" &&
        m?.fieldName === "WorkAddress"
      ) {
        if (field.id === "City") {
          eduCity = typeof apiValue === "string" ? apiValue : undefined;
          if (changed) {
            eduCityChanged = true;
          }
        } else if (field.id === "State") {
          eduState = typeof apiValue === "string" ? apiValue : undefined;
          if (changed) {
            eduStateChanged = true;
          }
        }
        // Do not write WorkAddress directly here; it will be composed
        // from eduCity/eduState after all fields are processed.
        return;
      }

      if (!changed) return;

      if (m?.destination === "userProfile" && m?.fieldName) {
        if (apiValue !== undefined) {
          userProfileProps[m.fieldName] = apiValue;
        }
      }
      if (m?.destination === "DFC" && m?.fieldName) {
        if (apiValue !== undefined) {
          DFCProps[m.fieldName] = apiValue;
        }
      }
      if (m?.destination === "educationHistory" && m?.fieldName) {
        if (apiValue !== undefined) {
          EducationHistoryProps[m.fieldName] = apiValue;
        }
      }
      if (m?.destination === "workHistory" && m?.fieldName) {
        if (apiValue !== undefined) {
          WorkHistoryProps[m.fieldName] = apiValue;
        }
      }
      if (m?.destination === "applicants" && m?.fieldName) {
        if (apiValue !== undefined) {
          ApplicantsProps[m.fieldName] = apiValue;
        }
      }
      if (m?.destination === "PBAP" && m?.fieldName) {
        if (apiValue !== undefined) {
          PBAPProps[m.fieldName] = apiValue;
        }
      }
      if (m?.destination === "RBT" && m?.fieldName) {
        if (apiValue !== undefined) {
          RBTProps[m.fieldName] = apiValue;
        }
      }
      if (m?.destination === "RBTC" && m?.fieldName) {
        if (apiValue !== undefined) {
          RBTCProps[m.fieldName] = apiValue;
        }
      }
      if (m?.destination === "AWSR" && m?.fieldName) {
        if (apiValue !== undefined) {
          AWSRProps[m.fieldName] = apiValue;
        }
      }
    });
  });

  if (errors.length) {
    const e: any = new Error(errors.join(" | "));
    e.type = "warning";
    throw e;
  }

  // Combine Education History City + State into WorkAddress for updates
  // Only when at least one of them actually changed to avoid
  // unnecessary updates.
  if ((eduCityChanged || eduStateChanged) && (eduCity || eduState)) {
    const parts: string[] = [];
    if (eduCity) parts.push(eduCity);
    if (eduState) parts.push(eduState);
    const combined = parts.join(", ");
    if (combined) {
      EducationHistoryProps["WorkAddress"] = combined;
    }
  }

  // If no changes detected, surface friendly info
  if (
    Object.keys(userProfileProps).length === 0 &&
    Object.keys(DFCProps).length === 0 &&
    Object.keys(EducationHistoryProps).length === 0 &&
    Object.keys(WorkHistoryProps).length === 0 &&
    Object.keys(ApplicantsProps).length === 0 &&
    Object.keys(PBAPProps).length === 0 &&
    Object.keys(RBTProps).length === 0 &&
    Object.keys(RBTCProps).length === 0 &&
    Object.keys(AWSRProps).length === 0
  ) {
    return { type: "info", message: "No Changes Detected" };
  }

  // Validate routing number via API if it's being updated (for banking info)
  // Both US and Canada paths use the same API validation
  // - Canada: institution (4) + transit (4) combined as 0IIIITTTT (9 digits)
  // - US: single routing number (9 digits)
  const routingNumberField = fields.find((f) => f.id === "routingNumber");
  const routingNumberValue = userProfileProps["RoutingNumber"];
  if (routingNumberField && routingNumberValue) {
    try {
      const response = await validateRoutingNumber(routingNumberValue);

      if (!response.data.isValid) {
        const e: any = new Error(
          "Invalid routing number. Please verify and try again.",
        );
        e.type = "warning";
        throw e;
      }

      // If routing number is valid, update bank name
      if (response.data.bankName) {
        userProfileProps["Bankname"] = response.data.bankName;
      }
    } catch (err: any) {
      // Stop everything if routing number validation fails
      const e: any = new Error(
        err?.message || "Error validating routing number. Please try again.",
      );
      e.type = "error";
      throw e;
    }
  }

  // Validate NPI# via API if it's being updated (for credentials)
  // Only validate if the NPI field changed
  const npiField = fields.find((f) => f.id === "npi");
  const npiValue = userProfileProps["NPI"];
  if (npiField && npiValue) {
    try {
      const response = await validateNPI(npiValue);

      if (!response.data.isValid) {
        const e: any = new Error("Invalid NPI#. Please verify and try again.");
        e.type = "warning";
        throw e;
      }
    } catch (err: any) {
      // Stop everything if NPI validation fails
      const e: any = new Error(
        err?.message || "Error validating NPI#. Please try again.",
      );
      e.type = "error";
      throw e;
    }
  }

  try {
    if (Object.keys(userProfileProps).length > 0) {
      if (
        !loginName ||
        typeof loginName !== "string" ||
        loginName.trim() === ""
      ) {
        const e: any = new Error(
          "Login name is required to update user profile",
        );
        e.type = "warning";
        throw e;
      }
      if (!siteUrl || typeof siteUrl !== "string") {
        const e: any = new Error("Site URL is required");
        e.type = "warning";
        throw e;
      }
      // Strip POSITIVE\ prefix if present
      const cleanLoginName = loginName?.replace(/^POSITIVE\\/, "");
      await updateUserProfileForCurrentUser(cleanLoginName, {
        properties: userProfileProps,
        siteUrl,
      });
    }

    if (Object.keys(ApplicantsProps).length > 0) {
      if (!siteUrl || typeof siteUrl !== "string") {
        const e: any = new Error("Site URL is required");
        e.type = "warning";
        throw e;
      }
      const cleanLoginName = loginName?.replace(/^POSITIVE\\/, "");
      try {
        await updateListItems({
          siteUrl,
          listName: "Applicants",
          filter: {
            AssignedLoginName: cleanLoginName,
          },
          fieldValues: ApplicantsProps,
          updateMultipleLimit: 1,
        });
      } catch (appErr: any) {
        if (
          appErr?.response?.data?.detail &&
          appErr.response.data.detail.includes("ITEM_NOT_FOUND")
        ) {
          await addListItem({
            siteUrl,
            listName: "Applicants",
            fieldValues: { ...ApplicantsProps, UserAccount: cleanLoginName },
          });
        } else {
          throw appErr;
        }
      }
    }

    if (Object.keys(DFCProps).length > 0) {
      if (!siteUrl || typeof siteUrl !== "string") {
        const e: any = new Error("Site URL is required");
        e.type = "warning";
        throw e;
      }
      const cleanLoginName = loginName?.replace(/^POSITIVE\\/, "");
      try {
        await updateListItems({
          siteUrl,
          listName: "Directory for Coordinators",
          filter: {
            NTName: `${cleanLoginName}`,
          },
          fieldValues: DFCProps,
          updateMultipleLimit: 1,
        });
      } catch (dfcErr: any) {
        if (
          dfcErr?.response?.data?.detail &&
          dfcErr.response.data.detail.includes("ITEM_NOT_FOUND")
        ) {
          await addListItem({
            siteUrl,
            listName: "Directory for Coordinators",
            fieldValues: { ...DFCProps, User1: cleanLoginName },
          });
        } else {
          throw dfcErr;
        }
      }
    }

    if (Object.keys(EducationHistoryProps).length > 0) {
      if (typeof itemId !== "number" || isNaN(itemId)) {
        const e: any = new Error(
          "Education History updates require a valid item ID.",
        );
        e.type = "warning";
        throw e;
      }
      if (!siteUrl || typeof siteUrl !== "string") {
        const e: any = new Error("Site URL is required");
        e.type = "warning";
        throw e;
      }
      await updateListItems({
        siteUrl,
        listName: "Schools and Education",
        filter: { ID: itemId },
        fieldValues: EducationHistoryProps,
        updateMultipleLimit: 1,
      });
    }
    if (Object.keys(WorkHistoryProps).length > 0) {
      if (typeof itemId !== "number" || isNaN(itemId)) {
        const e: any = new Error(
          "Work History updates require a valid item ID.",
        );
        e.type = "warning";
        throw e;
      }
      if (!siteUrl || typeof siteUrl !== "string") {
        const e: any = new Error("Site URL is required");
        e.type = "warning";
        throw e;
      }
      await updateListItems({
        siteUrl,
        listName: "Work Experience",
        filter: { ID: itemId },
        fieldValues: WorkHistoryProps,
        updateMultipleLimit: 1,
      });
    }

    if (Object.keys(PBAPProps).length > 0) {
      if (!siteUrl || typeof siteUrl !== "string") {
        const e: any = new Error("Site URL is required");
        e.type = "warning";
        throw e;
      }
      // Strip POSITIVE\ prefix if present
      const cleanLoginName = loginName?.replace(/^POSITIVE\\/, "");
      try {
        await updateListItems({
          siteUrl,
          listName: "Participants of Behavior Analysis Program",
          filter: {
            Person: `${cleanLoginName}`,
          },
          fieldValues: PBAPProps,
          updateMultipleLimit: 1,
        });
      } catch (pbapErr: any) {
        if (
          pbapErr?.response?.data?.detail &&
          pbapErr.response.data.detail.includes("ITEM_NOT_FOUND")
        ) {
          await addListItem({
            siteUrl,
            listName: "Participants of Behavior Analysis Program",
            fieldValues: { ...PBAPProps, Person: cleanLoginName },
          });
        } else {
          throw pbapErr;
        }
      }
    }

    if (Object.keys(RBTProps).length > 0) {
      if (!siteUrl || typeof siteUrl !== "string") {
        const e: any = new Error("Site URL is required");
        e.type = "warning";
        throw e;
      }
      // Strip POSITIVE\ prefix if present
      const cleanLoginName = loginName?.replace(/^POSITIVE\\/, "");
      try {
        await updateListItems({
          siteUrl,
          listName: "Registered Behavior Technicians",
          filter: {
            Name: `${cleanLoginName}`,
          },
          fieldValues: RBTProps,
          updateMultipleLimit: 100, // RBT list can have multiple entries per user, so allow updating multiple items if needed
        });
      } catch (rbtErr: any) {
        if (
          rbtErr?.response?.data?.detail &&
          rbtErr.response.data.detail.includes("ITEM_NOT_FOUND")
        ) {
          await addListItem({
            siteUrl,
            listName: "Registered Behavior Technicians",
            fieldValues: { ...RBTProps, Name: `${cleanLoginName}` },
          });
        } else {
          throw rbtErr;
        }
      }
    }

    if (Object.keys(RBTCProps).length > 0) {
      if (!siteUrl || typeof siteUrl !== "string") {
        const e: any = new Error("Site URL is required");
        e.type = "warning";
        throw e;
      }
      // Strip POSITIVE\ prefix if present
      const cleanLoginName = loginName?.replace(/^POSITIVE\\/, "");
      try {
        await updateListItems({
          siteUrl,
          listName: "RBT Candidate Tracking",
          filter: {
            Name: `${cleanLoginName}`,
          },
          fieldValues: RBTCProps,
          updateMultipleLimit: 1,
        });
      } catch (rbtcErr: any) {
        if (
          rbtcErr?.response?.data?.detail &&
          rbtcErr.response.data.detail.includes("ITEM_NOT_FOUND")
        ) {
          await addListItem({
            siteUrl,
            listName: "RBT Candidate Tracking",
            fieldValues: { ...RBTCProps, Name: cleanLoginName },
          });
        } else {
          throw rbtcErr;
        }
      }
    }

    if (Object.keys(AWSRProps).length > 0) {
      if (!siteUrl || typeof siteUrl !== "string") {
        const e: any = new Error("Site URL is required");
        e.type = "warning";
        throw e;
      }
      const cleanLoginName = loginName?.replace(/^POSITIVE\\/, "");
      try {
        await updateListItems({
          siteUrl,
          listName: "Analysts with Supervision Requirements",
          filter: {
            Name: `${cleanLoginName}`,
          },
          fieldValues: AWSRProps,
          updateMultipleLimit: 1,
        });
      } catch (awsrErr: any) {
        // If item not found, create it instead
        if (
          awsrErr?.response?.data?.detail &&
          awsrErr.response.data.detail.includes("ITEM_NOT_FOUND")
        ) {
          await addListItem({
            siteUrl,
            listName: "Analysts with Supervision Requirements",
            fieldValues: { ...AWSRProps, Name: cleanLoginName },
          });
        } else {
          throw awsrErr;
        }
      }
    }
  } catch (err: any) {
    const e: any = new Error(
      err?.message || "Error updating profile. Please try again.",
    );
    e.type = "error";
    throw e;
  }

  const updatedTargets: string[] = [];
  if (Object.keys(userProfileProps).length > 0)
    updatedTargets.push("User Profile");
  if (Object.keys(DFCProps).length > 0)
    updatedTargets.push("Directory for Coordinators");
  if (Object.keys(EducationHistoryProps).length > 0)
    updatedTargets.push("Education History");
  if (Object.keys(WorkHistoryProps).length > 0)
    updatedTargets.push("Work History");
  if (Object.keys(ApplicantsProps).length > 0)
    updatedTargets.push("Applicants");
  if (Object.keys(PBAPProps).length > 0)
    updatedTargets.push("Particiapant Behavior Analysis Program");
  if (Object.keys(RBTProps).length > 0)
    updatedTargets.push("Registered Behavior Technicians");
  if (Object.keys(RBTCProps).length > 0)
    updatedTargets.push("RBT Candidate Tracking");
  if (Object.keys(AWSRProps).length > 0)
    updatedTargets.push("Analysts with Supervision Requirements");

  const subject = updatedTargets.length
    ? updatedTargets.length === 1
      ? updatedTargets[0]
      : `${updatedTargets.slice(0, -1).join(", ")} and ${updatedTargets[updatedTargets.length - 1]}`
    : "Profile";
  const verb = updatedTargets.length > 1 ? "have" : "has";
  return {
    type: "success",
    message: `${subject} ${verb} been updated successfully.`,
  };
}
